import { Block } from '@components/core-ui/Block';
import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { Select } from '@components/core-ui/form-elements/Select';
import { DropZone } from '@components/Files/DropZone';
import { FilePreview } from '@components/Files/FilePreview';
import { useUploadContext } from '@components/Files/UploadProvider';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { secondsToHHMMSS } from '@helpers/secondsToHHMMSS';
import { sanitizeFileName } from '@helpers/sanitizeFileName';
import { useAPI } from '@providers/useAPI';
import { mapCopiedRepositoryMediaToFiles } from '@pages/User/NewJobPage/helpers/mapCopiedRepositoryMediaToFiles';
import { usePageContext } from '@pages/User/NewJobPage/providers/PageProvider';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation } from '@tanstack/react-query';
import { MediaService } from 'app/API';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { client } from 'app/http';
import { EACCEPTED_AUDIO_FORMATS } from 'app/types/enums/EACCEPTED_AUDIO_FORMATS';
import { useCallback, useState } from 'react';
import { ItemInterface, ReactSortable } from 'react-sortablejs';
import { useDownloadJobMedia } from './hooks/useDownloadJobMedia';
import { StyledBlock } from './styles/StyledBlock';
import { StyledLink } from './styles/StyledLink';

const MAX_DURATION_SECONDS = 10 * 60 * 60;

export const AudioFilesBlock = () => {
  const { call } = useAPI();
  const { jobId, form, isLoading, setFormItemValue, ensureDraftJobBeforeUpload } = usePageContext();
  const { files, attachExisting, reorderFiles } = useUploadContext();

  const multipleFilesOptions = [
    { value: 'concat', label: 'Concatenate' },
    { value: 'mixdown', label: 'Mixdown' },
  ];

  const [repositoryFiles, setRepositoryFiles] = useState<Record<string, unknown>[]>([]);

  const { openModal, closeModal } = useModalContext();
  const { workspace } = useAuthContext();
  const downloadJobMedia = useDownloadJobMedia(jobId);

  const copyRepositoryFilesMutation = useMutation({
    mutationFn: (requestBody: Parameters<typeof MediaService.copyRepositoryAttachmentsAsMedia>[0]['requestBody']) =>
      call(() => MediaService.copyRepositoryAttachmentsAsMedia({ requestBody })),
    onError: notifyApiCallError,
  });

  const handleBeforeAddFiles = useCallback(async () => {
    const ensuredJobId = await ensureDraftJobBeforeUpload();
    if (!ensuredJobId) {
      throw new Error('Failed to create draft job');
    }
  }, [ensureDraftJobBeforeUpload]);

  const openCaseFilesModal = () => {
    if (!workspace?.id) {
      return;
    }

    openModal(ModalNamesEnum.SelectCaseFiles, {
      repositoryId: form.item?.repositoryId,
      selectedFiles: repositoryFiles,
      setSelectedFiles: setRepositoryFiles,
      onSuccess: async (
        selectedFiles: {
          id: string;
          fileName: string;
          fileSize: string | number;
          weight?: number;
          audioDuration?: number | null;
        }[],
      ) => {
        const ensuredJobId = await ensureDraftJobBeforeUpload();
        if (!ensuredJobId) {
          return;
        }
        await copyRepositoryFilesMutation
          .mutateAsync({
            files: selectedFiles.map((f) => ({
              fileName: f.fileName,
              fileSize: String(f.fileSize),
              weight: f.weight ?? 0,
            })),
            jobId: ensuredJobId,
            workspaceId: workspace.id,
            repositoryId: form.item?.repositoryId ?? '',
          })
          .then(
            (files) => {
              attachExisting(mapCopiedRepositoryMediaToFiles(files));
              closeModal();
            },
            () => undefined,
          );
      },
      onCancel: closeModal,
    });
  };

  const handleSelectChange = async (name: string, value: string) => {
    setFormItemValue(name, value);
    setRepositoryFiles([]);
  };

  const handleRemoveRemote = useCallback(
    async (meta: { extra?: { mediaId?: string }; name?: string; status?: string }) => {
      const mediaId = meta.extra?.mediaId;
      const fileName = meta.name;
      if (!fileName) {
        return;
      }
      const safeFileName = sanitizeFileName(fileName);
      if (mediaId) {
        await client.delete('media', {
          headers: {
            'X-JobId': jobId,
            'X-MediaId': safeFileName,
            'X-Id': mediaId,
          },
        });
        return;
      }
      if (meta.status === 'done' || meta.status === 'external') {
        await client.delete('media', {
          headers: {
            'X-JobId': jobId,
            'X-MediaId': safeFileName,
          },
        });
      }
    },
    [jobId],
  );

  const activeFiles = files.filter((f) => f.meta.status !== 'removed');
  const sortableList: ItemInterface[] = activeFiles.map((file) => ({ id: file.meta.id }));
  const setSortableList = (list: ItemInterface[]) => {
    reorderFiles(list.map((item) => String(item.id)));
  };
  const totalDuration = activeFiles
    .map((f) => f.meta.extra?.duration ?? f.meta.duration ?? 0)
    .reduce((a, b) => a + b, 0);

  const dur = (f: (typeof activeFiles)[0]) => f.meta.extra?.duration ?? f.meta.duration ?? 0;
  const allDone = activeFiles.every((f) => f.meta.status === 'done' || f.meta.status === 'external');
  const allHaveDuration = activeFiles.every((f) => Number.isFinite(dur(f)) && dur(f) > 0);
  const isDurationMoreThan10Hrs = (() => {
    if (!activeFiles.length || !allDone || !allHaveDuration) {
      return false;
    }
    const isMixdown = (form.item?.multipleFilesHandling ?? 'concat') === 'mixdown';
    const durations = activeFiles.map(dur);
    const total = durations.reduce((s, d) => s + d, 0);
    const limit = isMixdown ? Math.max(...durations) : total;
    return limit > MAX_DURATION_SECONDS;
  })();

  const renderFilePreview = (item: (typeof activeFiles)[0]) => {
    const hasMediaId = Boolean(item.meta.extra?.mediaId);
    const isExternal = item.meta.status === 'external';
    const canDownload = hasMediaId || isExternal;
    const erroredState = item.meta.status === 'error' || item.meta.extra?.analysisStatus === 'error';
    const processingState =
      item.meta.status === 'preparing' ||
      item.meta.status === 'uploading' ||
      item.meta.extra?.analysisStatus === 'progress';
    const allowRemoveFile = !processingState || erroredState;

    return (
      <FilePreview
        item={item}
        allowRemove={allowRemoveFile}
        allowRetry
        allowDownload={canDownload}
        onDownload={downloadJobMedia}
        onRemoveRemote={handleRemoveRemote}
      />
    );
  };

  const renderMultipleFilesConcatChoices = () => {
    const isTrm = files.find((file) => file.meta?.name?.toLowerCase().includes('.trm'));
    if (files.length < 2 || isTrm) {
      return null;
    }
    const currentValue = form.item?.multipleFilesHandling ?? 'concat';
    const currentOptionDescription =
      currentValue === 'concat'
        ? 'Files are combined sequentially, based on the order they were uploaded, from top to bottom. Simply drag and drop to rearrange the files in the correct order.'
        : 'Files are combined in parallel. Select this option when each file is a separate recording of one of the participants.';
    return (
      <div className="multipleFilesHandling">
        <Select
          name="multipleFilesHandling"
          label="Files handling"
          value={currentValue}
          disabled={isLoading}
          onChange={handleSelectChange}
          options={multipleFilesOptions}
          required={true}
        />
        <div className="description">{currentOptionDescription}</div>
      </div>
    );
  };

  return (
    <div>
      <Block title="Audio Files">
        <DropZone multiple={true} accept={EACCEPTED_AUDIO_FORMATS.join(',')} onBeforeAddFiles={handleBeforeAddFiles} />

        {(activeFiles.length < 2 ? activeFiles : []).map((item) => (
          <div key={item.meta.id} className="mt-2">
            {renderFilePreview(item)}
          </div>
        ))}
        {activeFiles.length >= 2 ? (
          <ReactSortable list={sortableList} setList={setSortableList} animation={200} delay={1}>
            {activeFiles.map((item) => (
              <div key={item.meta.id} className="mt-2">
                {renderFilePreview(item)}
              </div>
            ))}
          </ReactSortable>
        ) : null}

        {activeFiles.length && allDone && allHaveDuration && form.item?.multipleFilesHandling !== 'mixdown' ? (
          <div className="uploadDuration">
            Total audio duration: <span>{secondsToHHMMSS(totalDuration)}</span>
          </div>
        ) : null}

        <div className="uploadDescription">
          <ul>
            <li>Zoom multi-channel audio, For The Record (FTR), MP3, WAV and other audio formats</li>
            {form.item?.repositoryId && !activeFiles.length ? (
              <li>
                <StyledLink onClick={openCaseFilesModal}>Or choose audio files from the Case</StyledLink>
              </li>
            ) : null}
          </ul>
        </div>
        {renderMultipleFilesConcatChoices()}
      </Block>
      {isDurationMoreThan10Hrs ? (
        <StyledBlock title="" description={<></>}>
          <ErrorMessage error="<span>The AutoScript platform cannot process audio files which exceed 10 hours in duration. Please contact <a style='color: var(--color-link);' href='mailto:support@voicescript.ai'>support</a> if you require assistance with splitting your audio files.</span>" />
        </StyledBlock>
      ) : null}
    </div>
  );
};
